import { settlementService } from '@/api/settlement.service';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { computed, type MaybeRefOrGetter, toValue } from 'vue';

const QUERY_KEY = 'settlement';

const BATCH_PAGE_SIZE = 5;
const UNSETTLED_PAGE_SIZE = 20;
const CANDIDATES_PAGE_SIZE = 20;

export const useSettlementCashFlow = (
  legalEntityId: MaybeRefOrGetter<string | null>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: computed(() => [QUERY_KEY, 'cash-flow', toValue(legalEntityId)]),
    queryFn: () => settlementService.getCashFlowOverview(toValue(legalEntityId) ?? undefined),
    enabled: computed(() => toValue(enabled)),
  });
};

export const useSettlementBatches = (
  legalEntityId: MaybeRefOrGetter<string | null>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useInfiniteQuery({
    queryKey: computed(() => [QUERY_KEY, 'batches', toValue(legalEntityId)]),
    queryFn: ({ pageParam }) =>
      settlementService.getProcessedBatches({
        page: pageParam as number,
        limit: BATCH_PAGE_SIZE,
        legalEntityId: toValue(legalEntityId) ?? undefined,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    enabled: computed(() => toValue(enabled)),
  });
};

export const useSettlementBatch = (
  batchId: MaybeRefOrGetter<string | null>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'batch', batchId],
    queryFn: () => settlementService.getProcessedBatch(toValue(batchId)!),
    enabled: computed(() => toValue(enabled) && toValue(batchId) != null),
    retry: false,
  });
};

export const useSettlementUnsettledPayments = (
  search: MaybeRefOrGetter<string>,
  legalEntityId: MaybeRefOrGetter<string | null>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useInfiniteQuery({
    queryKey: computed(() => [
      QUERY_KEY,
      'unsettled-payments',
      toValue(search),
      toValue(legalEntityId),
    ]),
    queryFn: ({ pageParam }) =>
      settlementService.getUnsettledPayments({
        page: pageParam as number,
        limit: UNSETTLED_PAGE_SIZE,
        q: toValue(search) || undefined,
        legalEntityId: toValue(legalEntityId) ?? undefined,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    enabled: computed(() => toValue(enabled)),
  });
};

export const useSettlementBatchDetails = (
  batchId: MaybeRefOrGetter<string | null>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useQuery({
    queryKey: [QUERY_KEY, 'batch-details', batchId],
    queryFn: () => settlementService.getBatchDetails(toValue(batchId)!),
    enabled: computed(() => toValue(enabled) && toValue(batchId) != null),
  });
};

export const useSettlementPaymentCandidates = (
  detailId: MaybeRefOrGetter<string | null>,
  search: MaybeRefOrGetter<string>,
  enabled: MaybeRefOrGetter<boolean> = true,
) => {
  return useInfiniteQuery({
    queryKey: computed(() => [QUERY_KEY, 'payment-candidates', toValue(detailId), toValue(search)]),
    queryFn: ({ pageParam }) =>
      settlementService.getPaymentCandidates({
        detailId: toValue(detailId)!,
        page: pageParam as number,
        limit: CANDIDATES_PAGE_SIZE,
        q: toValue(search) || undefined,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.meta.nextPage,
    enabled: computed(() => toValue(enabled) && !!toValue(detailId)),
  });
};

export const useAssignSettlementPayment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ detailId, paymentId }: { detailId: string; paymentId: string }) =>
      settlementService.assignPayment(detailId, paymentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};
